/**
 * 词表树选择器（S08）—— 左侧浏览 + 顶部搜索，选中后把整条节点数据交给调用方。
 *
 * 为什么单独抽组件：S10（URDF/关节元数据）和 S11（资产建版本）都要"选一个标准件分类"，
 * 页面里再写一遍树等于把词表逻辑复制三份。这里只负责「选节点」，不负责展示规格字段。
 *
 * 数据只拉一次 `/api/taxonomy/tree`（133 行、几十 KB），搜索在前端做：
 * 词表是小字典，每敲一个字打一次接口反而慢。
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AutoComplete, Button, Empty, Input, Space, Spin, Tag, Tree, Typography } from "antd";
import { DownOutlined, SearchOutlined, UpOutlined } from "@ant-design/icons";
import { callApi } from "../lib/api";
import { AsyncBoundary } from "./AsyncBoundary";

export interface SpecField {
  key: string;
  label: string;
  type: "number" | "text" | "select" | "boolean";
  unit?: string;
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

export interface SpecSchema {
  summary?: string;
  fields: SpecField[];
}

export interface TaxonomyNode {
  id: string;
  parent_id: string | null;
  source_id: string | null;
  name: string;
  path_text: string;
  depth: number;
  sort: number;
  spec_schema: SpecSchema | null;
  children?: TaxonomyNode[];
}

interface TreeData {
  tree: TaxonomyNode[];
  total: number;
  max_depth: number;
  default_expanded_depth: number;
}

interface Props {
  selectedId?: string | null;
  onSelect: (node: TaxonomyNode) => void;
  /** 只看配了规格模板的节点（含其祖先链） */
  onlyWithSpec?: boolean;
  /** 树区域高度，默认 480 */
  height?: number;
}

/** 深度优先展平，顺带记层级路径，供搜索下拉展示。 */
function flatten(nodes: TaxonomyNode[], out: TaxonomyNode[] = []): TaxonomyNode[] {
  for (const n of nodes) {
    out.push(n);
    if (n.children?.length) flatten(n.children, out);
  }
  return out;
}

export function TaxonomyPicker({ selectedId, onSelect, onlyWithSpec = false, height = 480 }: Props) {
  const q = useQuery({
    queryKey: ["taxonomy", "tree"],
    queryFn: () => callApi<TreeData>("/api/taxonomy/tree"),
    staleTime: 5 * 60 * 1000,
  });

  const raw = q.data?.data;
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);

  const nodes = useMemo(() => (raw ? flatten(raw.tree) : []), [raw]);
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  // 首次拿到数据：默认展开到第 4 级（展开 depth ≤ 3 的节点 → 第 4 级可见）
  useEffect(() => {
    if (!raw || touched) return;
    const depth = raw.default_expanded_depth || 4;
    setExpandedKeys(nodes.filter((n) => n.depth < depth).map((n) => n.id));
  }, [raw, nodes, touched]);

  const filtered = useMemo(() => {
    if (!onlyWithSpec) return raw?.tree ?? [];
    const pick = (list: TaxonomyNode[]): TaxonomyNode[] =>
      list
        .map((n) => ({ ...n, children: pick(n.children ?? []) }))
        .filter((n) => (n.children?.length ?? 0) > 0 || n.spec_schema);
    return pick(raw?.tree ?? []);
  }, [raw, onlyWithSpec]);

  const treeData = useMemo(() => {
    const conv = (list: TaxonomyNode[]): Array<Record<string, unknown>> =>
      list.map((n) => ({
        key: n.id,
        // 叶子节点用 name，父节点带子节点数，便于判断"要不要展"
        title: (
          <Space size={4}>
            <span>{n.name}</span>
            {n.spec_schema && (
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                规格
              </Tag>
            )}
            {n.children?.length ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {n.children.length}
              </Typography.Text>
            ) : null}
          </Space>
        ),
        children: n.children?.length ? conv(n.children) : undefined,
        isLeaf: !n.children?.length,
      }));
    return conv(filtered);
  }, [filtered]);

  const [keyword, setKeyword] = useState("");
  const suggestions = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return [];
    return nodes
      .filter((n) => (!onlyWithSpec || n.spec_schema) && (n.name.toLowerCase().includes(k) || n.path_text.toLowerCase().includes(k)))
      .slice(0, 20)
      .map((n) => ({
        value: n.id,
        label: (
          <Space size={6}>
            <span>{n.name}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {n.path_text}
            </Typography.Text>
          </Space>
        ),
      }));
  }, [keyword, nodes, onlyWithSpec]);

  const expandAll = () => {
    setTouched(true);
    setExpandedKeys(nodes.filter((n) => n.children?.length).map((n) => n.id));
  };
  const collapseAll = () => {
    setTouched(true);
    setExpandedKeys(raw ? raw.tree.map((n) => n.id) : []);
  };

  return (
    <AsyncBoundary loading={q.isLoading} error={q.error}>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <AutoComplete
          value={keyword}
          options={suggestions}
          onChange={setKeyword}
          onSelect={(id) => {
            const node = byId.get(String(id));
            if (node) {
              // 选中后把祖先链展开，否则树上看不到它
              const segs = node.path_text.split(/(?<!\\)\//);
              const keys: string[] = [];
              for (let i = 1; i <= segs.length; i++) {
                const hit = nodes.find((n) => n.path_text === segs.slice(0, i).join("/"));
                if (hit) keys.push(hit.id);
              }
              setTouched(true);
              setExpandedKeys((prev) => [...new Set([...prev, ...keys])]);
              onSelect(node);
              setKeyword("");
            }
          }}
          style={{ width: "100%" }}
        >
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="搜节点名或路径，如 马达 / 标准件/机械标准件"
          />
        </AutoComplete>

        <Space size={8}>
          <Button size="small" icon={<DownOutlined />} onClick={expandAll}>
            展开全部
          </Button>
          <Button size="small" icon={<UpOutlined />} onClick={collapseAll}>
            收起到一级
          </Button>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            共 {raw?.total ?? 0} 个节点，最深 {raw?.max_depth ?? "-"} 级
          </Typography.Text>
        </Space>

        {treeData.length === 0 ? (
          <Empty description="没有匹配的节点" />
        ) : (
          <div style={{ maxHeight: height, overflow: "auto", border: "1px solid #f0f0f0", borderRadius: 6, padding: 8 }}>
            <Tree
              blockNode
              showLine
              treeData={treeData}
              expandedKeys={expandedKeys}
              selectedKeys={selectedId ? [selectedId] : []}
              onExpand={(keys) => {
                setTouched(true);
                setExpandedKeys(keys as string[]);
              }}
              onSelect={(keys) => {
                const id = String(keys[0] ?? "");
                const node = byId.get(id);
                if (node) onSelect(node);
              }}
            />
          </div>
        )}
        {q.isFetching && (
          <Spin size="small">
            <span style={{ fontSize: 12, color: "#999" }}> 同步中…</span>
          </Spin>
        )}
      </Space>
    </AsyncBoundary>
  );
}
